import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProviderById,
  getUserFavouriteProviderIds,
  toggleProviderFavourite,
  createProviderIssueReport,
} from "./utils/providerNetworkApi";
import ReportProviderIssueModal from "./components/ReportProviderIssueModal";
import { ReactComponent as BookmarkIcon } from "../../../assets/icons/providerNetwork/Bookmark.svg";
import { ReactComponent as BookmarkFilledIcon } from "../../../assets/icons/providerNetwork/Bookmark (filled).svg";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import "../../../css/ProviderNetwork/ProviderNetworkDetailsPage.css";
import "../../../css/ProviderNetwork/ProviderNetwork.css";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { useUserEmail } from "../../../context/UserEmailContext";
import { ReactComponent as EmailIcon } from "../../../assets/icons/providerNetwork/Email.svg";
import { ReactComponent as PhoneIcon } from "../../../assets/icons/providerNetwork/Contact.svg";
import { ReactComponent as WebsiteIcon } from "../../../assets/icons/providerNetwork/Website.svg";
import { ReactComponent as AddressIcon } from "../../../assets/icons/providerNetwork/Location(filled).svg";
import { ReactComponent as AcceptsDigitalSignIcon } from "../../../assets/icons/providerNetwork/AcceptsDigitalSign.svg";
import { ReactComponent as AcceptsScannedDocsIcon } from "../../../assets/icons/providerNetwork/AcceptsScannedDocs.svg";
import { ReactComponent as AcceptsUnipassIcon } from "../../../assets/icons/providerNetwork/AcceptsUnipass.svg";
import { ReactComponent as DoesntAcceptDigitalSignIcon } from "../../../assets/icons/providerNetwork/DoesntAcceptDigitalSign.svg";
import { ReactComponent as DoesntAcceptScannedDocsIcon } from "../../../assets/icons/providerNetwork/DoesntAcceptScannedDocs.svg";
import { ReactComponent as LoaPostOnlyIcon } from "../../../assets/icons/providerNetwork/LOAPostOnly.svg";
import { ReactComponent as SecureEmailIcon } from "../../../assets/icons/providerNetwork/SecureEmail.svg";
import { ReactComponent as WetSignOnlyIcon } from "../../../assets/icons/providerNetwork/WetSignOnly.svg";
import { ReactComponent as HelpIcon } from "../../../assets/icons/providerNetwork/HelpIcon.svg";
import { ReactComponent as CollapseIcon } from "../../../assets/icons/expand-arrow.svg";

const DEBUG_PROVIDER_DETAILS = true;

const log = (...args) => {
  if (DEBUG_PROVIDER_DETAILS) {
    // console.log("[ProviderDetailsPage]", ...args);
  }
};

const yesNo = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "-";
};

const asArray = (value, fallbackValue = "") => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  if (fallbackValue) return [fallbackValue];
  return [];
};

const ProviderDetailsPage = () => {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const { userEmail } = useUserEmail();
  const planRefs = useRef({});
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [isFavourite, setIsFavourite] = useState(false);

  const breadcrumbItems = [
    { label: "LOA Toolkit", to: "/portal/loa-toolkit" },
    { label: "Provider Index", to: "/provider-index" },
    { label: provider?.providerName || "Provider Details" },
  ];

  useEffect(() => {
    log("Mounted / params changed", {
      providerId,
      userEmail,
      path: window.location.pathname,
    });

    const fetchProvider = async () => {
      if (!providerId) {
        console.warn(
          "[ProviderDetailsPage] No providerId found in URL params.",
        );
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        log("Fetching provider and favourites...", { providerId, userEmail });

        const [providerData, favouriteIds] = await Promise.all([
          getProviderById(providerId),
          getUserFavouriteProviderIds(userEmail),
        ]);

        log("Provider data returned:", providerData);
        log("Favourite ids returned:", favouriteIds);

        setProvider(providerData);
        setIsFavourite(favouriteIds.includes(providerId));

        if (providerData?.plans?.length) {
          log("Plans found:", providerData.plans);
          log("Setting first selected plan:", providerData.plans[0].id);
          setSelectedPlanId(providerData.plans[0].id);
        } else {
          console.warn(
            "[ProviderDetailsPage] Provider loaded but no plans found:",
            providerData,
          );
          setSelectedPlanId("");
        }
      } catch (err) {
        // console.error("[ProviderDetailsPage] Failed to load provider:", err);
        // alert(err.message || "Failed to load provider.");
      } finally {
        // log("Fetch complete. Setting loading false.");
        setLoading(false);
      }
    };

    fetchProvider();
  }, [providerId, userEmail]);

  const handlePlanTabClick = (planId) => {
    setSelectedPlanId(planId);

    const target = planRefs.current[planId];

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  // const selectedPlan = useMemo(() => {
  //   log("Calculating selectedPlan", {
  //     hasProvider: Boolean(provider),
  //     selectedPlanId,
  //     plans: provider?.plans,
  //   });

  //   if (!provider?.plans?.length) return null;

  //   const plan =
  //     provider.plans.find((item) => item.id === selectedPlanId) ||
  //     provider.plans[0];

  //   log("Selected plan:", plan);

  //   return plan;
  // }, [provider, selectedPlanId]);

  const handleToggleFavourite = async () => {
    try {
      log("Toggling favourite", {
        providerId,
        isFavourite,
        userEmail,
      });

      await toggleProviderFavourite(providerId, isFavourite, userEmail);
      setIsFavourite((prev) => !prev);
    } catch (err) {
      // console.error("[ProviderDetailsPage] Failed to update bookmark:", err);
      // alert(err.message || "Failed to update bookmark.");
    }
  };

  const handleOpenIssueModal = () => {
    setIssueModalOpen(true);
  };

  const handleCloseIssueModal = () => {
    if (issueSubmitting) return;
    setIssueModalOpen(false);
  };

  const handleSubmitIssueReport = async ({
    providerName,
    issueType,
    details,
    reportedBy,
    submittedAt,
  }) => {
    try {
      setIssueSubmitting(true);
      setIssueModalOpen(false);
      const payload = {
        providerId,
        providerName: providerName || provider?.providerName || "",
        issueType,
        details,
        reportedBy:
          reportedBy || userEmail || localStorage.getItem("userEmail") || "",
        submittedAt: submittedAt || new Date().toISOString(),
      };

      //  console.log("[ProviderDetailsPage] Submitting issue report:", payload);

      await createProviderIssueReport(payload);

      //  setIssueModalOpen(false);
    } catch (error) {
      //  console.error(
      //    "[ProviderDetailsPage] Error submitting issue report:",
      //    error,
      //  );
      //  alert(error.message || "Unable to submit issue report. Please try again.");
    } finally {
      setIssueSubmitting(false);
    }
  };
  const renderContent = () => {
    log("Render state", {
      loading,
      provider,
      // selectedPlan,
      selectedPlanId,
      isFavourite,
    });

    if (loading) {
      return <div className="provider-network-empty">Loading provider...</div>;
    }

    if (!provider) {
      return (
        <div className="provider-network-empty">
          Provider not found. Check the console for providerId and Firebase
          logs.
        </div>
      );
    }

    return (
      <div className="provider-network-details-page">
        <Breadcrumb items={breadcrumbItems} />

        <div className="provider-details-header">
          <div className="provider-details-heading">
            <div className="provider-details-logo-box">
              {provider.providerLogoUrl ? (
                <img
                  src={provider.providerLogoUrl}
                  alt={provider.providerName}
                  className="provider-details-logo"
                />
              ) : (
                <div className="provider-details-logo-fallback">
                  {provider.providerName?.slice(0, 2)?.toUpperCase() || "PN"}
                </div>
              )}
            </div>

            <div>
              <div className="FP_H3">{provider.providerName}</div>

              <div className="FP_Help_Text">
                {provider.planCount || provider.plans?.length || 0} Record
                {(provider.planCount || provider.plans?.length || 0) === 1
                  ? ""
                  : "s"}
              </div>
            </div>
          </div>

          <div className="provider-details-actions">
            <button
              type="button"
              className="provider-details-action-btn FP_Content"
              onClick={handleToggleFavourite}
            >
              {isFavourite ? <BookmarkFilledIcon /> : <BookmarkIcon />}
              {isFavourite ? "Bookmarked provider" : "Bookmark provider"}
            </button>

            <div className="provider-details-report-issue FP_Content">
              <HelpIcon /> Missing or incorrect info?{" "}
              <button
                type="button"
                className="provider-details-report-issue-btn FP_Content_Semibold"
                onClick={handleOpenIssueModal}
              >
                Report an issue
              </button>
            </div>

            {/* <button
              type="button"
              className="provider-details-action-btn FP_Content"
              onClick={() => navigate(`/ProviderNetwork/Edit/${providerId}`)}
            >
              <EditIcon />
              Edit details
            </button> */}
          </div>
        </div>
        {!!provider.plans?.length && (
          <div className="provider-details-plan-tabs">
            {provider.plans.map((plan) => (
              <button
                key={plan.id}
                type="button"
                className={`provider-details-plan-tab ${
                  selectedPlanId === plan.id ? "is-active" : ""
                }`}
                onClick={() => handlePlanTabClick(plan.id)}
              >
                {plan.planName || "Unnamed Plan"}
              </button>
            ))}
          </div>
        )}

        {!provider.plans?.length ? (
          <div className="provider-network-empty">
            No plan/policy details found.
          </div>
        ) : (
          <div className="provider-details-plan-list">
            {provider.plans.map((plan) => (
              <div
                key={plan.id}
                ref={(el) => {
                  planRefs.current[plan.id] = el;
                }}
                className="provider-details-plan-scroll-target"
              >
                <ProviderPlanDetails provider={provider} plan={plan} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>{provider?.providerName || "Provider Network"}</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container Lavender provider-network">
        {renderContent()}
      </div>

      <ReportProviderIssueModal
        isOpen={issueModalOpen}
        provider={provider}
        onClose={handleCloseIssueModal}
        onSubmit={handleSubmitIssueReport}
        loading={issueSubmitting}
      />
    </div>
  );
};

const ProviderPlanDetails = ({ provider, plan }) => {
  const [isOpen, setIsOpen] = useState(true);

  log("Rendering ProviderPlanDetails", { provider, plan });

  const addresses = asArray(plan.addresses, plan.address);
  const emails = asArray(plan.emails, plan.email);
  const websites = asArray(plan.websites, plan.website);

  const contacts = Array.isArray(plan.contacts)
    ? plan.contacts.filter((contact) => contact?.number)
    : plan.contact?.number
      ? [plan.contact]
      : [];

  const loaGuidelines = plan.loaGuidelines || {};

  const guidelineRows = [
    {
      label:
        loaGuidelines.acceptsScannedDocs === false
          ? "Does not accept scanned docs"
          : "Accepts scanned docs",
      value: loaGuidelines.acceptsScannedDocs,
      icon:
        loaGuidelines.acceptsScannedDocs === false ? (
          <DoesntAcceptScannedDocsIcon />
        ) : (
          <AcceptsScannedDocsIcon />
        ),
    },
    {
      label:
        loaGuidelines.acceptsLoaByPostOnly === false
          ? "Does not require LOAs by post only"
          : "Accepts LOAs by post only",
      value: loaGuidelines.acceptsLoaByPostOnly,
      icon: <LoaPostOnlyIcon />,
    },
    {
      label:
        loaGuidelines.acceptsDigitallySignedDocs === false
          ? "Does not accept digitally signed docs"
          : "Accepts digitally signed docs",
      value: loaGuidelines.acceptsDigitallySignedDocs,
      icon:
        loaGuidelines.acceptsDigitallySignedDocs === false ? (
          <DoesntAcceptDigitalSignIcon />
        ) : (
          <AcceptsDigitalSignIcon />
        ),
    },
    {
      label:
        loaGuidelines.acceptsWetSignatureOnly === false
          ? "Does not require wet signature only"
          : "Accepts wet signature only",
      value: loaGuidelines.acceptsWetSignatureOnly,
      icon: <WetSignOnlyIcon />,
    },
    {
      label:
        loaGuidelines.acceptsLoaInsteadOfAuthority === false
          ? "Does not accept Unipass Letter of Authority"
          : "Accepts Unipass Letter of Authority",
      value: loaGuidelines.acceptsLoaInsteadOfAuthority,
      icon: <AcceptsUnipassIcon />,
    },
    {
      label:
        loaGuidelines.secureEmail === false
          ? "No secure email"
          : "Secure Email",
      value: loaGuidelines.secureEmail,
      icon: <SecureEmailIcon />,
    },
  ];

  const visibleGuidelines = guidelineRows.filter(
    (row) => row.value === true || row.value === false,
  );

  return (
    <div className="provider-details-plan-card">
      <button
        type="button"
        className="provider-details-plan-card-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="provider-plan-details">
          <div className="FP_Subtitle_Semibold provider-details-plan-name primary">
            {plan.planName || "Unnamed Plan/Policy"}
          </div>
          {plan.planType && (
            <div className="FP_Help_Text provider-details-plan-provider-name">
              {plan.planType}
            </div>
          )}
        </div>

        <CollapseIcon
          className={`provider-details-plan-collapse-icon ${
            isOpen ? "is-open" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="provider-details-plan-card-body">
          <div className="provider-details-content provider-details-content-updated">
            <div className="provider-details-main-card provider-details-main-card-updated">
              <div className="provider-details-info-grid">
                {plan.policyStartingWith && (
                  <DetailList
                    label="Policy Starting With"
                    values={[plan.policyStartingWith]}
                    // icon={<PolicyIcon />}
                  />
                )}
                {emails.length > 0 && (
                  <DetailList
                    label="Email"
                    values={emails}
                    type="email"
                    icon={<EmailIcon />}
                  />
                )}

                {contacts.length > 0 && (
                  <DetailContactList contacts={contacts} icon={<PhoneIcon />} />
                )}

                {websites.length > 0 && (
                  <DetailList
                    label="Website / Online Portal"
                    values={websites}
                    type="link"
                    icon={<WebsiteIcon />}
                  />
                )}

                {addresses.length > 0 && (
                  <DetailList
                    label="Address"
                    values={addresses}
                    icon={<AddressIcon />}
                  />
                )}
              </div>
            </div>

            <div className="provider-details-side provider-details-side-updated">
              {!!visibleGuidelines.length && (
                <div className="provider-details-guidelines-card">
                  <div className="FP_Body_Semibold provider-details-side-title">
                    LOA Guidelines
                  </div>

                  <div className="provider-details-guideline-list">
                    {visibleGuidelines.map((row) => (
                      <GuidelineRow
                        key={row.label}
                        label={row.label}
                        value={row.value}
                        icon={row.icon}
                      />
                    ))}
                  </div>
                </div>
              )}

              {plan.notes && (
                <div className="provider-details-notes-card">
                  <div className="FP_Help_Text provider-details-side-title">
                    Additional Notes
                  </div>

                  <div className="provider-details-row-value FP_Content black">
                    {(  plan.notes || "").split("\n").map((line, index) => (
                      <p key={index} className="FP_Content black" style={{ marginBottom: "5px" }}>
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {!!plan.attachments?.length && (
                <div className="provider-details-attachments-card">
                  <div className="FP_Body_Semibold provider-details-side-title">
                    Attachments
                  </div>

                  {plan.attachments.map((file, index) => (
                    <a
                      key={`${file.name}-${index}`}
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      className="provider-details-attachment-link"
                    >
                      {file.name || `Attachment ${index + 1}`}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailList = ({ label, values = [], type = "text", icon = null }) => {
  const safeValues = values.filter(Boolean);

  return (
    <div className="provider-details-row">
      <div className="provider-details-row-label FP_Help_Text is-flex">
        {icon}
        {label}
      </div>

      {safeValues.length ? (
        <div className="provider-details-value-list">
          {safeValues.map((value, index) => {
            if (type === "link") {
              const href = value.startsWith("http")
                ? value
                : `https://${value}`;

              return (
                <a
                  key={`${label}-${index}`}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="provider-details-row-value FP_Content"
                >
                  {value}
                </a>
              );
            }

            if (type === "email") {
              return (
                <a
                  key={`${label}-${index}`}
                  href={`mailto:${value}`}
                  className="provider-details-row-value FP_Content"
                >
                  {value}
                </a>
              );
            }

            return (
              <div
                key={`${label}-${index}`}
                className="provider-details-row-value FP_Content black"
              >
                {value}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="provider-details-row-value FP_Content black">-</div>
      )}
    </div>
  );
};

const DetailContactList = ({ contacts = [], icon = null }) => {
  return (
    <div className="provider-details-row">
      <div className="provider-details-row-label FP_Help_Text is-flex">
        {icon}
        Contact
      </div>

      {contacts.length ? (
        <div className="provider-details-value-list">
          {contacts.map((contact, index) => {
            const contactValue = `${contact.countryCode || ""} ${
              contact.number || ""
            }`.trim();

            return contactValue ? (
              <a
                key={`contact-${index}`}
                href={`tel:${contactValue.replace(/\s/g, "")}`}
                className="provider-details-row-value FP_Content"
              >
                {contactValue}
              </a>
            ) : (
              <div
                key={`contact-${index}`}
                className="provider-details-row-value FP_Content black"
              >
                -
              </div>
            );
          })}
        </div>
      ) : (
        <div className="provider-details-row-value FP_Content black">-</div>
      )}
    </div>
  );
};

const GuidelineRow = ({ label, value, icon }) => {
  return (
    <div className="provider-details-guideline-row">
      <div className="provider-details-guideline-left FP_Content">
        {icon && (
          <span className="provider-details-guideline-icon">{icon}</span>
        )}
        <span>{label}</span>
      </div>

      {/* <strong>{yesNo(value)}</strong> */}
    </div>
  );
};

export default ProviderDetailsPage;
